import type { ReactElement } from 'react';
import {
  ACTIVITY_VIA_TEMPLATE,
  activityAnchor,
  type Notification as NotificationRowData,
} from '@molt/shared';
import { Circle, CircleDot } from 'lucide-react';
import { Avatar, IconButton, MarkdownBody, RelativeTime } from '@/components';
import {
  inlineReplyState,
  notificationHref,
  notificationSentence,
} from '@/lib/notifications';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { NotificationReply } from './NotificationReply';

/**
 * `FR-NOTIF-009` — *"opens the card detail it refers to, scrolled to the
 * relevant comment **or activity entry** where applicable"*.
 *
 * `notificationHref` (in `@/lib/notifications`, not this chunk's file) already
 * appends `#${commentAnchor(id)}` when the row carries a comment; the "or
 * activity entry" half had no id to scroll to until
 * `notificationSchema.activityEntryId` reached the wire, so this composes the
 * second anchor around the shipped function rather than restating it.
 *
 * ⚠ **The branch is on the DATA, never on the returned string.**
 * `notificationHref` is not this chunk's file: sniffing its result for a `#`
 * would change meaning silently the day it appends a query or a second
 * fragment. `comment === null && activityEntryId !== null` is the same question
 * asked of the fields the anchor is built from.
 *
 * ⚠ **Card hrefs only.** `/c/<shortLink>#activity-<id>` is verbatim what
 * `ActivityEntryItem` builds and what `CommentSection` reads back out of
 * `window.location.hash` to highlight, so that shape is precedented and
 * consumed end to end. A board href (`/b/<shortLink>`) gets no fragment:
 * `BoardActivitySection` highlights from its own state, and shipping a
 * fragment nothing reads would be decoration a reader would later mistake for
 * a contract.
 */
function notificationTarget(row: NotificationRowData): string | null {
  const base = notificationHref(row);
  if (base === null) return null;
  if (row.card === null) return base;
  if (row.comment !== null) return base;
  if (row.activityEntryId === null) return base;
  return `${base}#${activityAnchor(row.activityEntryId)}`;
}

/**
 * `FE-70.4`, `FR-AUTO-016` AC3, `FR-COLLAB-017`'s edge case — the notification
 * sentence under the name of the tool that caused it.
 *
 * ⚠ **`actor` and `via` are independent and both are rendered.** An automation
 * acting on a member's behalf keeps the member as `actor` (AC3: *"the comment
 * appears under the acting member's name and avatar **and is distinguishable
 * from a comment they typed**"*), so `actor !== null && via !== null` is the
 * ordinary shape of a rule-run row. The avatar above stays the member's; this
 * adds the distinguishing half.
 *
 * ⚠ **`ACTIVITY_VIA_TEMPLATE` reused, and `renderActivitySentenceVia` NOT
 * reused.** That helper takes `{templateKey, tokens}` and re-renders an
 * *activity* sentence; a notification sentence comes from the notification
 * catalogue and is already rendered. Reusing the constant keeps one
 * translatable string (`packages/shared/src/activity.ts`) and adds no key to
 * the interface catalogue, which is not this chunk's file.
 *
 * ⚠⚠ **ONE pass, through a CALLBACK, and both halves matter** — the idiom is
 * copied from `renderActivitySentenceVia`, not just its constant. A callback
 * replacement is inserted verbatim, so a card titled `$&` or `$'` inside the
 * rendered sentence cannot reach `String.replace`'s pattern syntax; one pass
 * over the template means the substituted sentence is never rescanned, so a
 * card titled `{tool}` cannot become the tool's name. Two chained
 * `.replace('{sentence}', …)` calls fail both ways, silently, on data a member
 * can type.
 *
 * The blank guard is the caller's (`viaOf` below) for the reason the shared
 * helper throws on a blank name: *"archived this card, via "* is grammatical
 * enough to read past, valid enough to render, and wrong.
 */
function withVia(sentence: string, tool: string): string {
  return ACTIVITY_VIA_TEMPLATE.replace(/\{([A-Za-z0-9_]+)\}/g, (_match, slot: string) => {
    if (slot === 'sentence') return sentence;
    if (slot === 'tool') return tool;
    // Unreachable while the constant is the two slots it declares, and loud
    // rather than silent if a third is added to it.
    throw new Error(`notification via template needs a value for {${slot}}`);
  });
}

/** `notificationSchema.via`, or `null` for a person, no entry, or a blank name. */
function viaOf(row: NotificationRowData): string | null {
  if (row.via === null) return null;
  const tool = row.via.trim();
  return tool === '' ? null : tool;
}

export interface NotificationRowProps {
  readonly notification: NotificationRowData;
  readonly onToggleRead: (id: string, read: boolean) => void;
  readonly onOpenNotification: (
    notification: NotificationRowData,
  ) => Promise<void> | void;
  readonly onNavigate?: (href: string) => void;
}

export function NotificationRow({
  notification,
  onToggleRead,
  onOpenNotification,
  onNavigate = (href) => {
    window.location.assign(href);
  },
}: NotificationRowProps): ReactElement {
  const { catalogue, t } = useInterfaceLanguage();
  const href = notificationTarget(notification);
  const via = viaOf(notification);
  const rendered = notificationSentence(notification, catalogue, t);
  const sentence = via === null ? rendered : withVia(rendered, via);
  /*
   * `FE-17.4` / `FR-NOTIF-009` AC3 — `notificationBoardRefSchema.readable`.
   *
   * ⚠ **A CLASSIFICATION, never a filter and never a disabled row.**
   * `FR-NOTIF-001` AC3 requires the *removed from the board* notification to
   * arrive and render, so the row keeps its text, its excerpt, its timestamp
   * and its read toggle. Before this field reached the wire the state was not
   * assertable at all: `closed` and `deleted` are facts about the *board* and
   * neither says anything about *this reader*, so a former member of a live
   * board saw `closed: false, deleted: false` and nothing else.
   *
   * ⚠ **The anchor is deliberately KEPT for an unreadable board**, and that is
   * a measured decision rather than an omission. `NotificationDrawer` refuses
   * the left click at the navigation seam — the path `FR-NOTIF-009` AC3 is
   * about — and renders `card.notAvailable.body`, *"You no longer have access,
   * or this card is no longer available."* Replacing the anchor with a button
   * would close only the middle-click and cmd-click paths, would take the
   * permalink away from a member who may legitimately want to ask for access
   * back, and would substitute a different behaviour for the requirement rather
   * than closing it. No new interface string is added here (this chunk owns
   * neither catalogue), so the row classifies and `NotificationReply` says the
   * sentence in place of the composer.
   *
   * ⚠ **Where an escaping middle click lands, verified per href shape — the
   * two shapes differ and only one is covered.** A CARD-scoped row's
   * `/c/<shortLink>` reaches `routes/c.$shortLink`'s `not-found` branch, which
   * renders `card.notAvailable.title`/`.body` — the same sentence, at the far
   * end. A BOARD-scoped row's `/b/<shortLink>` (`card === null`, which is
   * `removed_from_board`'s own shape) reaches `routes/b.$shortLink`'s
   * `not-found` branch, which renders `BoardError` carrying the **server's**
   * `ApiError.message` — literally *"No such board"*. That copy is already a
   * reported, unpatched defect in that route's own header (an existence oracle
   * in prose, `apps/server`'s to fix); it is recorded here as a named residual
   * for the surface that owns it, and it does not change the treatment on this
   * row, because the drawer catches the left click for both shapes.
   */
  const boardAccess =
    notification.board === null
      ? undefined
      : notification.board.readable
        ? 'readable'
        : 'revoked';
  /*
   * ⚠ **`readable` is folded into `inlineReplyState` now, in
   * `@/lib/notifications`, beside the deleted-board, closed-board and
   * deleted-card branches it belongs with** — it is no longer taken here and
   * handed to `NotificationReply` as a prop. Fan-out 7 routed it that way
   * because `lib/notifications.ts` was outside its file list; two authors of
   * one fact is how the two drift. This row asks once and renders one of three
   * things, and `'noAccess'` is a MESSAGE rather than silence because
   * `FR-NOTIF-009` AC3 asks for one.
   */
  const replyState = inlineReplyState(notification);
  const markLabel = notification.read
    ? t('notifications.markUnread')
    : t('notifications.markRead');
  /*
   * ⚠⚠ **AN HREF THAT MUST NEVER BE FOLLOWED IS NOT RENDERED AS AN HREF, AND
   * THAT IS THE FIX RATHER THAN A TIGHTER CLICK HANDLER.**
   *
   * This row used to render a real `<a href>` for every reachable target and
   * `preventDefault()` the click. That closes the plain left click and the
   * cmd-click (both dispatch `click`, which React hears) and closes **nothing
   * else**: a middle click dispatches `auxclick`, and "Open link in new tab"
   * and "Copy link address" never dispatch anything at all. For a board the
   * recipient can no longer read, all three escaped the drawer's refusal and
   * followed the raw href — and a board-scoped row's `/b/<shortLink>` lands in
   * `routes/b.$shortLink`'s `not-found`, which renders `BoardError` carrying
   * the server's own `ApiError.message`: literally *"No such board"*. That is
   * an existence oracle in prose. A caller outside a read scope must not be
   * able to tell "you cannot see it" from "there is no such thing", and the
   * whole server answers 404 for exactly that reason.
   *
   * ⚠ The card-scoped shape (`/c/<shortLink>`) did **not** leak: that route's
   * `not-found` renders `card.notAvailable.body` — *"You no longer have access,
   * or this card is no longer available."* — which is deliberately fused and
   * says nothing either way. It becomes a button too, because an href refused
   * on one gesture and honoured on another is the bug, not the copy.
   *
   * ⚠ **The shape stays CONDITIONAL: a readable row keeps its real anchor.**
   * Middle-click, cmd-click and "copy link address" on a permalink are things a
   * member legitimately wants, and taking the anchor from every row would be a
   * different product decision rather than a fix.
   *
   * ⚠ **`href` is still computed for the unreadable row and still handed to
   * `onNavigate`**, which is why `NotificationDrawer` needs no change (and is
   * not this chunk's file): its `isUnreachableBoard` guard fires on the same
   * callback it always did, sets `accessRefused` and renders the sentence.
   */
  const boardUnreachable = notification.board !== null && !notification.board.readable;
  const openSource = async () => {
    if (href === null) return;
    try {
      await onOpenNotification(notification);
    } finally {
      onNavigate(href);
    }
  };

  return (
    <article
      role="listitem"
      data-board-access={boardAccess}
      data-notification-via={via ?? undefined}
      className={
        notification.read
          ? 'rounded-card bg-surface px-2 py-2'
          : 'rounded-card bg-[color-mix(in_oklab,var(--color-accent)_10%,var(--color-surface))] px-2 py-2'
      }
    >
      <div className="flex gap-2">
        <IconButton
          label={markLabel}
          size="sm"
          onClick={() => onToggleRead(notification.id, !notification.read)}
        >
          {notification.read ? (
            <Circle aria-hidden="true" className="size-4" />
          ) : (
            <CircleDot aria-hidden="true" className="size-4" />
          )}
        </IconButton>

        {notification.actor === null ? (
          <Avatar profile={null} state="none" size="sm" decorative />
        ) : (
          <Avatar profile={notification.actor} size="sm" decorative />
        )}

        <div className="min-w-0 flex-1">
          {href === null ? (
            <div className="rounded-card px-1 py-0.5">
              <NotificationText
                sentence={sentence}
                createdAt={notification.createdAt}
                read={notification.read}
              />
              <p className="mt-1 text-xs text-ink-muted">
                {t('notifications.sourceUnavailable')}
              </p>
            </div>
          ) : boardUnreachable ? (
            <button
              type="button"
              /*
               * ⚠ **MEASURED, NOT PRECAUTIONARY.** The first browser run of
               * this control found it in the accessibility tree as
               * `button [ref=…]` with **no name at all**, its paragraph and its
               * timestamp exposed as children — while the `<a href>` beside it
               * takes its name from exactly the same content and is located by
               * that name in six shipped e2e assertions. So an unnamed button
               * is what withdrawing the href would have shipped: a control a
               * screen reader announces as "button", in the one surface whose
               * whole job is to say what happened.
               *
               * The label is `sentence` — the already-rendered, already-
               * translated string, so no catalogue key is added and no second
               * spelling of the same sentence can drift from the first. It also
               * *improves* on name-from-content, which would glue the relative
               * timestamp ("2m") onto the end of the label.
               *
               * ⚠ The anchor branch below is deliberately NOT given one. Its
               * name resolves today and is pinned by six e2e locators and a
               * capture; changing a working accessible name is a separate
               * decision from giving one to a control that has none.
               */
              aria-label={sentence}
              className="block w-full rounded-card px-1 py-0.5 text-left hover:bg-surface-raised"
              onClick={() => {
                void openSource();
              }}
            >
              <NotificationText
                sentence={sentence}
                createdAt={notification.createdAt}
                read={notification.read}
              />
            </button>
          ) : (
            <a
              href={href}
              className="block rounded-card px-1 py-0.5 hover:bg-surface-raised"
              onClick={(event) => {
                event.preventDefault();
                void openSource();
              }}
            >
              <NotificationText
                sentence={sentence}
                createdAt={notification.createdAt}
                read={notification.read}
              />
            </a>
          )}

          {notification.comment !== null ? (
            <MarkdownBody
              markdown={`${notification.comment.excerpt}${notification.comment.truncated ? '...' : ''}`}
              className="mt-1 text-xs text-ink-muted"
              clamp={{ maxHeight: 64, moreLabel: t('notifications.showFullExcerpt') }}
            />
          ) : null}

          {replyState === 'open' ? (
            <NotificationReply
              cardId={notification.card!.id}
              boardId={notification.board!.id}
            />
          ) : replyState === 'noAccess' ? (
            /*
             * `FR-NOTIF-009` AC3 — *"a 'you no longer have access' message"*.
             * The message lives here rather than inside `NotificationReply`
             * because the composer's gate moved into `inlineReplyState`: a
             * component that is never rendered cannot say why. `data-reply-
             * access` keeps both of its values so the two states stay
             * distinguishable to a test and to a capture.
             *
             * ⚠ `notifications.replyNoAccess`, NOT `sourceUnavailable`. The
             * old sentence described the object ("This source is no longer
             * available."); AC3 asks for one about the reader, and a former
             * member of a live, open board is looking at a card that is
             * perfectly available.
             */
            <p className="mt-2 text-xs text-ink-muted" data-reply-access="none">
              {t('notifications.replyNoAccess')}
            </p>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function NotificationText({
  sentence,
  createdAt,
  read,
}: {
  readonly sentence: string;
  readonly createdAt: string;
  readonly read: boolean;
}) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-2">
      <p className="min-w-0 text-sm text-ink">
        <span className={read ? 'font-normal' : 'font-medium'}>{sentence}</span>
      </p>
      <RelativeTime value={createdAt} className="shrink-0 text-xs text-ink-muted" />
    </div>
  );
}
